import React, { useState } from "react";
import { Field, Formik, Form, ErrorMessage } from "formik";
import styles from "../Login.module.scss";
import LoginLayout from "../LoginLayout";
import Button from "components/Button/Button";
import {
  FormControl,
  FormFeedback,
  FormField,
  FormLabel,
  FormLink,
  FormRow,
} from "components/Form/Form";
import Dialog, {
  DialogDescription,
  DialogTitle,
} from "components/Dialog/Dialog";
import AccountMigratedModal from "components/AccountMigratedModal/AccountMigratedModal";
import { api, login, handleAuthRedirect } from "services";

import toast from "react-hot-toast";
import logos from "assets/images/canaleducacao/Logotipos-Canal-Acerela-Seduc.png";
import { useParams } from "react-router-dom";
import logoCanal from "assets/images/canaleducacao/horizontal-logo.svg";
import logoSeduc from "assets/images/canaleducacao/logo-seduc-horizontal.png";

export default function SignIn() {
  const [showModal, setShowModal] = useState(false);
  const [showAccountMigratedModal, setShowAccountMigratedModal] =
    useState(false);
  const { link, connection } = useParams();

  const institutionalDomains = [
    "@professor.edu.pi.gov.br",
    "@aluno.edu.pi.gov.br",
  ];

  const isInstitutionalEmail = (email) =>
    institutionalDomains.some((domain) => email.toLowerCase().endsWith(domain));

  const authenticate = async (values) => {
    const response = await login(values);
    if (response.status === 200) {
      const { token, found_db, client_url } = response.data;

      if (token && found_db && client_url) {
        handleAuthRedirect({ token, found_db, client_url });
        return;
      } else {
        toast.error("Erro ao autenticar");
        return response;
      }
    }
    return response;
  };

  const sendEmailConfirmation = async (values) => {
    const lastEmailSent = localStorage.getItem("lastEmailSent");
    if (lastEmailSent) {
      const now = new Date();
      const last = new Date(lastEmailSent);
      const diff = now - last;
      const diffSeconds = Math.floor(diff / 3000);
      if (diffSeconds < 30) {
        toast.error("Aguarde 5 minutos para reenviar o email de confirmação");
        return;
      }

      localStorage.setItem("lastEmailSent", new Date());
    } else {
      localStorage.setItem("lastEmailSent", new Date());
    }

    const toastLoading = toast.loading("Reenviando email de confirmação");

    if (!values.email) {
      return toast.error("Informe o email");
    }

    try {
      const response = await api.post("send_confirmation_email", {
        email: values.email,
      });
      if (response.status === 200) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.error);
    } finally {
      toast.dismiss(toastLoading);
    }
  };

  return (
    <LoginLayout
      back={link && connection ? `/login/${link}/${connection}` : "/login"}
    >
      <Formik
        initialValues={{ email: "", password: "", showPassword: false }}
        onSubmit={async (values, actions) => {
          let payload;
          if (link && connection) {
            payload = {
              ...values,
              classroom_invitation: link,
              connection,
            };
          } else {
            payload = values;
          }

          try {
            toast.loading("Autenticando...");
            const response = await authenticate(payload);
            toast.dismiss();

            if (response.status !== 200) {
              if (response.status === 401) {
                if (response.response.data.error?.unconfirmed) {
                  setShowModal(true);
                } else if (
                  response.response.data.error === "invalid credentials" ||
                  response.response.data.error.user_authentication ===
                    "invalid credentials"
                ) {
                  actions.setErrors({
                    email: "Email ou senha incorretos",
                    password: "Email ou senha incorretos",
                  });

                  if (!isInstitutionalEmail(payload.email)) {
                    setShowAccountMigratedModal(true);
                  }
                }
              }
            }
          } catch (err) {
            console.log(err);
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {(props) => (
          <>
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>Acesse sua conta</h1>
                  <FormRow>
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <FormControl
                      id="email"
                      name="email"
                      type="email"
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      value={props.values.email}
                      placeholder="Insira seu e-mail"
                      invalid={
                        (props.errors.email || props.errors.password) &&
                        props.touched.email
                      }
                    />
                    {props.errors.email || props.errors.password ? (
                      <ErrorMessage name="email">
                        {(msg) => (
                          <FormFeedback invalid={true}>{msg}</FormFeedback>
                        )}
                      </ErrorMessage>
                    ) : null}
                  </FormRow>

                  <FormRow>
                    <FormLabel htmlFor="password">Senha</FormLabel>
                    <FormControl
                      id="password"
                      name="password"
                      type={props.values.showPassword ? "text" : "password"}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      value={props.values.password}
                      invalid={
                        (props.errors.email || props.errors.password) &&
                        props.touched.password
                      }
                      placeholder="Insira sua senha"
                    />
                    {props.errors.email || props.errors.password ? (
                      <ErrorMessage name="email">
                        {(msg) => (
                          <FormFeedback invalid={true}>{msg}</FormFeedback>
                        )}
                      </ErrorMessage>
                    ) : null}

                    <FormField>
                      <Field type="checkbox" name="showPassword" />
                      Mostrar senha
                    </FormField>
                  </FormRow>

                  <FormRow>
                    <FormLink to="/esqueci-a-senha">
                      Esqueceu sua senha?
                    </FormLink>
                  </FormRow>
                </div>

                <Button
                  modifiers={["outline", "full"]}
                  type="submit"
                  disabled={props.isSubmitting}
                >
                  Entrar
                </Button>
                {/* RETORNAR APÓS PERÍODO ELEITORAL */}
                {/* <img src={logos} alt="Logos" /> */}
              </div>
            </Form>

            <Dialog show={showModal} onClose={() => setShowModal(false)}>
              <DialogTitle>Antes de entrar na plataforma...</DialogTitle>
              <DialogDescription>
                você precisa ativar sua conta. Clique no botão abaixo para
                reenviarmos o link para o seu email. Abra sua caixa de entrada e
                ative sua conta em segundos.
              </DialogDescription>

              <Button
                modifiers={["primary"]}
                onClick={() => sendEmailConfirmation(props.values)}
              >
                Reenviar email
              </Button>
            </Dialog>
          </>
        )}
      </Formik>

      <AccountMigratedModal
        isOpen={showAccountMigratedModal}
        onClose={() => setShowAccountMigratedModal(false)}
      />
    </LoginLayout>
  );
}
